<template>
  <div class="clause-container">
    <AppSectionHero
      title="Clause Library"
      subtitle="Store reusable legal wording, browse by category, and maintain approved language."
      :stats="heroStats"
      :show-refresh="false"
    />

    <CCard class="border-0 shadow-sm create-shell">
      <CCardBody class="p-4 create-shell__body">
        <div>
          <div class="create-shell__eyebrow">Start Here</div>
          <div class="create-shell__title">Need to add a new clause?</div>
          <div class="create-shell__copy">Create a reusable clause first, then manage it here alongside your approved library wording.</div>
        </div>
        <button v-if="canManageClauses" class="primary-btn create-shell__button" @click="openCreateModal">
          <CIcon name="cil-plus" />
          Add Clause
        </button>
        <div v-else class="create-shell__note">Clause management is available for admin and committee members only.</div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-3 p-lg-4">
        <div class="toolbar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input v-model="searchQuery" placeholder="Search clauses by title or text...">
          </div>
          <select v-model="selectedCategory" class="filter-select">
            <option v-for="item in categories" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm card-shell">
      <div class="card-header">
        <div>
          <div class="result-title">Clause Directory</div>
          <div class="result-copy">Showing {{ paginatedClauses.length }} of {{ filteredClauses.length }} clauses</div>
        </div>
        <span class="result-badge">{{ selectedCategoryLabel }}</span>
      </div>

      <CCardBody class="p-0">
        <div v-if="filteredClauses.length" class="clause-list">
          <div v-for="item in paginatedClauses" :key="item.id" class="clause-card">
            <div class="clause-card__header">
              <div class="clause-card__eyebrow">
                <span class="category-pill" :class="categoryClass(item.category)">{{ categoryName(item.category) }}</span>
                <span class="source-pill">{{ item.sourceType === 'default' ? 'Starter' : 'Custom' }}</span>
                <span class="clause-stat">Used {{ item.usageCount }} times</span>
              </div>
              <div class="clause-title">{{ item.title }}</div>
              <div class="clause-meta">
                <span>Added {{ formatDate(item.createdAt) }}</span>
                <span>Category {{ categoryName(item.category) }}</span>
              </div>
            </div>

            <div class="clause-content">{{ previewContent(item.content) }}</div>

            <div class="clause-card__footer">
              <button class="small-btn small-btn--ghost" @click="viewClause(item)">View</button>
              <button v-if="canManageClauses" class="small-btn" @click="editClause(item)">Edit</button>
              <button v-if="canManageClauses && item.editable" class="small-btn small-btn--danger" @click="deleteClause(item)">Delete</button>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <div class="empty-state__icon">📚</div>
          <div class="empty-state__title">No clauses found</div>
          <div class="empty-state__copy">Try adjusting the search or category filter.</div>
        </div>

        <div v-if="filteredClauses.length > pageSize" class="pagination">
          <button v-if="currentPage > 1" class="page-btn" @click="currentPage--">Previous</button>
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="showEditor" class="modal-overlay" @click.self="closeEditor">
      <div class="modal-card">
        <div class="modal-header">
          <h2>{{ editingClause ? 'Edit Clause' : 'Create Clause' }}</h2>
          <button class="close-btn" @click="closeEditor">✕</button>
        </div>
        <form class="editor-form" @submit.prevent="saveClause">
          <label>
            <span>Category</span>
            <select v-model="form.category" required>
              <option disabled value="">Select category</option>
              <option v-for="item in categories.slice(1)" :key="item.id" :value="item.name">{{ item.name }}</option>
            </select>
          </label>
          <label>
            <span>Title</span>
            <input v-model="form.title" required placeholder="Clause title">
          </label>
          <label>
            <span>Clause Text</span>
            <textarea v-model="form.content" rows="7" required placeholder="Enter the clause text"></textarea>
          </label>
          <div class="modal-actions">
            <button type="button" class="secondary-btn" @click="closeEditor">Cancel</button>
            <button type="submit" class="primary-btn">{{ editingClause ? 'Update Clause' : 'Save Clause' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="viewingClause" class="modal-overlay" @click.self="viewingClause = null">
      <div class="modal-card">
        <div class="modal-header">
          <h2>{{ viewingClause.title }}</h2>
          <button class="close-btn" @click="viewingClause = null">✕</button>
        </div>
        <div class="view-body">
          <div class="clause-meta">
            <span class="category-pill" :class="categoryClass(viewingClause.category)">{{ categoryName(viewingClause.category) }}</span>
            <span>Used {{ viewingClause.usageCount }} times</span>
            <span>Added {{ formatDate(viewingClause.createdAt) }}</span>
          </div>
          <div class="view-content">{{ viewingClause.content }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import Service from '@/service/api'
import { showConfirmAlert, showErrorAlert } from '@/utils/sweet-alert'
import { getAssignedRoles } from '@/utils/role-dashboard'

function categoryKey(value) {
  return String(value || 'General')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'general'
}

export default {
  name: 'ClauseLibraryPage',
  components: {
    AppSectionHero
  },
  data() {
    return {
      clauses: [],
      selectedCategory: 'all',
      searchQuery: '',
      currentPage: 1,
      pageSize: 8,
      showEditor: false,
      editingClause: null,
      viewingClause: null,
      form: {
        title: '',
        category: '',
        content: '',
        appliesTo: []
      },
      categories: [{ id: 'all', name: 'All Clauses' }]
    }
  },
  computed: {
    canManageClauses() {
      const roles = getAssignedRoles(this.$store)
      return roles.includes('admin') || roles.includes('committee')
    },
    heroStats() {
      return [
        { label: 'Total Clauses', value: this.clauses.length, hint: 'Reusable templates in the library', icon: 'cil-library', iconClass: 'app-section-stat__icon--total' },
        { label: 'Visible Now', value: this.filteredClauses.length, hint: 'Current search and category result', icon: 'cil-filter', iconClass: 'app-section-stat__icon--attention' },
        { label: 'Total Usage', value: this.totalUsageCount, hint: 'Combined clause reuse count', icon: 'cil-chart-line', iconClass: 'app-section-stat__icon--active' }
      ]
    },
    totalUsageCount() {
      return this.clauses.reduce((total, item) => total + Number(item.usageCount || 0), 0)
    },
    filteredClauses() {
      let rows = [...this.clauses]
      if (this.selectedCategory !== 'all') rows = rows.filter(item => categoryKey(item.category) === this.selectedCategory)
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        rows = rows.filter(item => item.title.toLowerCase().includes(query) || item.content.toLowerCase().includes(query))
      }
      return rows
    },
    paginatedClauses() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredClauses.slice(start, start + this.pageSize)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredClauses.length / this.pageSize))
    },
    selectedCategoryLabel() {
      return this.categoryName(this.selectedCategory)
    }
  },
  watch: {
    searchQuery() { this.currentPage = 1 },
    selectedCategory() { this.currentPage = 1 }
  },
  mounted() {
    this.loadClauses()
  },
  methods: {
    async loadClauses() {
      const response = await Service.agreements('user-clauses')
      const rows = response && response.data && response.data.data ? response.data.data : []
      this.clauses = rows.map(item => ({
        ...item,
        id: item && (item.id || item._id) ? String(item.id || item._id) : '',
        usageCount: Number(item.usageCount || 0),
        createdAt: item.createdAt || item.updatedAt || new Date().toISOString(),
        categoryKey: categoryKey(item.category)
      }))
      const categoryItems = [...new Set(this.clauses.map(item => String(item.category || 'General')).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right))
        .map(name => ({ id: categoryKey(name), name }))
      this.categories = [{ id: 'all', name: 'All Clauses' }].concat(categoryItems)
      if (this.selectedCategory !== 'all' && !this.categories.find(item => item.id === this.selectedCategory)) {
        this.selectedCategory = 'all'
      }
    },
    categoryName(id) {
      const item = this.categories.find(category => category.id === id || categoryKey(category.name) === id)
      return item ? item.name : id
    },
    categoryClass(value) {
      return 'category-pill--' + categoryKey(value)
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    previewContent(value) {
      const text = String(value || '').trim()
      if (text.length <= 220) return text
      return text.slice(0, 220).trim() + '...'
    },
    openCreateModal() {
      if (!this.canManageClauses) {
        showErrorAlert('Only admin and committee members can add clauses.', 'Access Restricted')
        return
      }
      this.editingClause = null
      this.form = { title: '', category: '', content: '', appliesTo: [] }
      this.showEditor = true
    },
    editClause(item) {
      if (!this.canManageClauses) {
        showErrorAlert('Only admin and committee members can edit clauses.', 'Access Restricted')
        return
      }
      this.editingClause = item
      this.form = { title: item.title, category: item.category, content: item.content, appliesTo: Array.isArray(item.appliesTo) ? item.appliesTo : [] }
      this.showEditor = true
    },
    viewClause(item) {
      this.viewingClause = item
    },
    closeEditor() {
      this.showEditor = false
      this.editingClause = null
    },
    async saveClause() {
      if (!this.canManageClauses) {
        showErrorAlert('Only admin and committee members can save clauses.', 'Access Restricted')
        return
      }
      const payload = {
        title: this.form.title,
        category: this.form.category,
        content: this.form.content,
        appliesTo: Array.isArray(this.form.appliesTo) && this.form.appliesTo.length ? this.form.appliesTo : ['MOU File']
      }
      if (this.editingClause) {
        await Service.agreements('update-clause', { id: this.editingClause.id || this.editingClause._id, ...payload })
      } else {
        await Service.agreements('create-clause', payload)
      }
      await this.loadClauses()
      this.closeEditor()
    },
    async deleteClause(item) {
      if (!this.canManageClauses) {
        showErrorAlert('Only admin and committee members can delete clauses.', 'Access Restricted')
        return
      }
      if (!item || !item.editable) {
        showErrorAlert('Starter clauses are read-only and cannot be deleted.', 'Read-Only Clause')
        return
      }
      const confirmed = await showConfirmAlert({
        title: 'Delete Clause?',
        text: `Delete "${item.title}"?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      })
      if (!confirmed) return
      await Service.agreements('delete-clause', { id: item.id || item._id })
      await this.loadClauses()
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("clause-container");
@include workspace-toolbar-search;
@include workspace-button-system;

.clause-container {
  padding: 24px;
}

.create-shell {
  background:
    radial-gradient(circle at right top, rgba(254, 194, 96, 0.16), transparent 30%),
    linear-gradient(135deg, rgba(140, 21, 21, 0.05) 0%, rgba(255, 255, 255, 0.96) 54%, rgba(255, 248, 237, 0.98) 100%);
  border: 1px solid #eadfce;
  border-radius: 22px;
  box-shadow: 0 16px 34px rgba(44, 52, 71, 0.08);
  margin-bottom: 16px;
}

.create-shell__body {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.create-shell__eyebrow {
  color: #8c1515;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.create-shell__title {
  color: #223045;
  font-size: 1.18rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.create-shell__copy {
  color: #66758a;
  line-height: 1.6;
  margin-top: 6px;
  max-width: 42rem;
}

.create-shell__button {
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(140, 21, 21, 0.16);
  min-height: 46px;
  min-width: 170px;
  padding: 10px 18px;
}

.create-shell__note {
  color: #66758a;
  font-size: 0.92rem;
  line-height: 1.6;
  max-width: 22rem;
  text-align: right;
}

.card-header,
.pagination,
.modal-header,
.modal-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.card-shell,
.modal-card {
  background: #fff;
  border: 1px solid #e9edf3;
  border-radius: 18px;
}

.card-header,
.pagination {
  padding: 18px 20px;
}

.result-title {
  color: #344767;
  font-size: 1rem;
  font-weight: 700;
}

.result-copy {
  color: #718096;
  font-size: 0.92rem;
  margin-top: 2px;
}

.result-badge {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 8px 12px;
}

.clause-list {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  padding: 0 20px 20px;
}

.clause-card {
  background: linear-gradient(180deg, #fffefc 0%, #ffffff 100%);
  border: 1px solid #eef1f6;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 18px;
}

.clause-card__header {
  display: grid;
  gap: 10px;
}

.clause-card__eyebrow,
.clause-meta,
.clause-card__footer {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.clause-title {
  color: #2d3748;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.clause-meta,
.clause-stat {
  color: #718096;
  font-size: 14px;
}

.category-pill {
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
}

.source-pill {
  background: #fff7ed;
  border-radius: 999px;
  color: #9a3412;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
}

.category-pill--validity { background: #eef2ff; color: #3730a3; }
.category-pill--termination { background: #fff1f2; color: #b42318; }
.category-pill--confidentiality { background: #ecfdf3; color: #027a48; }
.category-pill--ip-rights { background: #f7e8eb; color: #8a1728; }
.category-pill--dispute-resolution { background: #fff7e6; color: #9a6700; }
.category-pill--general,
.category-pill--academic,
.category-pill--financial,
.category-pill--legal { background: #f4f4f5; color: #52525b; }

.clause-content {
  color: #52606d;
  flex: 1;
  line-height: 1.75;
  margin-top: 14px;
  white-space: pre-wrap;
}

.clause-card__footer {
  margin-top: 18px;
}

.pagination {
  align-items: center;
  display: flex;
  justify-content: center;
  padding-bottom: 26px;
  position: relative;
}

.pagination .page-btn:first-child {
  left: 20px;
  position: absolute;
}

.pagination .page-btn:last-child {
  position: absolute;
  right: 20px;
}

.small-btn--danger {
  background: linear-gradient(135deg, #8c1515 0%, #b42318 100%);
  border-color: #8c1515;
  box-shadow: 0 10px 18px rgba(140, 21, 21, 0.16);
  color: #fff8f5;
}

.small-btn--danger:hover:not(:disabled),
.small-btn--danger:active:not(:disabled) {
  background: linear-gradient(135deg, #7a1111 0%, #a61f15 100%);
  border-color: #7a1111;
  color: #fff8f5;
}

.empty-state {
  align-items: center;
  color: #718096;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 36px 20px;
  text-align: center;
}

.empty-state__icon {
  font-size: 2rem;
}

.empty-state__title {
  color: #344767;
  font-size: 1.1rem;
  font-weight: 700;
}

.modal-overlay {
  align-items: center;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 1000;
}

.modal-card {
  max-width: 760px;
  padding: 22px;
  width: 100%;
}

.close-btn {
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 20px;
}

.editor-form {
  display: grid;
  gap: 14px;
}

.editor-form label {
  display: grid;
  gap: 8px;
}

.editor-form span {
  color: #344767;
  font-weight: 700;
}

.editor-form input,
.editor-form select,
.editor-form textarea {
  border: 1px solid #d8e0ea;
  border-radius: 12px;
  padding: 12px;
}

.view-body {
  display: grid;
  gap: 16px;
}

.view-content {
  color: #52606d;
  line-height: 1.75;
  white-space: pre-wrap;
}

@media (max-width: 720px) {
  .clause-container {
    padding: 16px;
  }

  .toolbar,
  .create-shell__body,
  .card-header,
  .pagination,
  .modal-header,
  .modal-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .search-box,
  .filter-select {
    width: 100%;
  }

  .clause-card__footer {
    width: 100%;
  }

  .create-shell__button {
    min-width: 100%;
    width: 100%;
  }

  .create-shell__note {
    max-width: none;
    text-align: left;
  }
}
</style>
